import { Link } from "react-router-dom";
import { Icon } from "./icons";
import { bcUrl } from "./config";
import { linksFor } from "./links";
import type { CompanyLink } from "./api";
import { SUITE, areaHref, areaNav, type BcPage, type Domain, type ExtLink } from "./suite";
import { areaOpen, useAccess } from "./security/store";

export function BcLink({ p, className, onClick, children }: { p: BcPage; className?: string; onClick?: () => void; children?: React.ReactNode }) {
  return (
    <a href={bcUrl(p.page)} target="_blank" rel="noopener noreferrer" className={className} onClick={onClick} title={`Open ${p.name} in Business Central`}>
      {children ?? p.name}
    </a>
  );
}

export function ExtA({ l, className, onClick, children }: { l: ExtLink; className?: string; onClick?: () => void; children?: React.ReactNode }) {
  let host = "";
  try { host = new URL(l.url).host; } catch {}
  return <a href={l.url} target="_blank" rel="noopener noreferrer" className={className} onClick={onClick} title={`${l.name} · ${host}`}>{children ?? l.name}</a>;
}

/** Default links for an area plus the company's own. */
export const areaLinks = (d: Domain, company: CompanyLink[]): ExtLink[] => [...(d.links ?? []), ...linksFor(company, d.id)];

/** One card per area, opening the area's home page. Each card says what's waiting on the person there (from My
 *  Work); the person's own area (by department) comes first. The launcher and Courage home (`home`, larger cards). */
const words = (d: Domain) => [d.name, d.summary, ...areaNav(d).map((p) => p.name), ...d.modules.filter((m) => m.status === "live" || m.status === "bc").map((m) => m.name)].join(" ").toLowerCase().replace(/[^a-z0-9]+/g, " ");

export function AreaGrid({ onPick, skip = [], waiting = {}, first = null, home = false, filter = "" }: { onPick?: () => void; skip?: string[]; waiting?: Record<string, number>; first?: string | null; home?: boolean; filter?: string }) {
  const access = useAccess();
  const terms = filter.toLowerCase().split(/\s+/).filter(Boolean);
  const areas = SUITE.filter((d) => !skip.includes(d.id) && areaOpen(access, d.id) && (!terms.length || terms.every((t) => (" " + words(d)).includes(" " + t))));
  // Typing narrows the areas; an area whose name starts with what was typed comes first.
  const named = terms.length ? areas.filter((d) => d.name.toLowerCase().startsWith(terms.join(" "))) : [];
  const lead = named.length ? named : first ? areas.filter((d) => d.id === first) : [];
  const ordered = [...lead, ...areas.filter((d) => !lead.includes(d))];
  if (!ordered.length) return <div className="empty">No area matches “{filter.trim()}”.</div>;
  return (
    <div className={`area-grid ${home ? "home" : ""}`}>
      {ordered.map((d) => {
        const live = d.modules.some((m) => m.status === "live");
        const n = waiting[d.id] ?? 0;
        return (
          <Link key={d.id} to={areaHref(d)} onClick={onPick} className={`area-card ${live ? "has-live" : ""}`} aria-label={`${d.name} home${n ? `, ${n} waiting on you` : ""}`}>
            <span className="ico"><Icon name={d.icon} size={home ? 20 : 18} /></span>
            <span className="body">
              <span className="name">{d.name}</span>
              <span className="desc">{d.summary}</span>
              {n > 0 && <span className="meta waiting"><b>{n}</b> waiting on you</span>}
            </span>
            {home && <span className="go" aria-hidden="true"><Icon name="chev" size={14} /></span>}
          </Link>
        );
      })}
    </div>
  );
}

/** @deprecated kept for older imports; the launcher and home use AreaGrid. */
export const SuiteGrid = AreaGrid;
